import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, Button } from "@mui/material";
import { useIntl } from "react-intl";

import AddIcon from "@mui/icons-material/Add";

import { TemplateItemProps } from "../types";
import { MeasRuleValue, Rule } from "./types";
import { uuidv4 } from "../../../../utils/utils";
import AlarmItem from "./AlarmItem";

interface AlarmListProps extends TemplateItemProps<Rule<MeasRuleValue[]>[]> {}

const AlarmList = ({ value, saved, onChange, disabled }: AlarmListProps) => {
  const [indexes, setIndexes] = useState<number[]>([]);
  const { formatMessage } = useIntl();

  const onAlarmChange = useCallback(
    (index: number, v: Rule<MeasRuleValue[]>) => {
      if (!value) {
        return;
      }
      value[index] = v;
      onChange(value);
    },
    [value, onChange]
  );

  const addNewAlarm = useCallback(() => {
    value.push({ uuid: uuidv4() });
    onChange(value);
    setIndexes(value.map((v, i) => i));
  }, [value, onChange]);

  const removeAlarm = useCallback(
    (index) => {
      value[index].deleted = true;
      onChange(value);
    },
    [value, onChange]
  );

  const restoreAlarm = useCallback(
    (index) => {
      if (!value) {
        return;
      }
      delete value[index].deleted;
      onChange(value);
    },
    [value, onChange]
  );

  useEffect(() => {
    setIndexes(value.map((_, i) => i));
  }, [value]);

  const reports = useMemo(
    () => (
      <Grid container spacing={1}>
        {indexes?.length === value.length &&
          indexes.map((i) => (
            <Grid item xs={12} key={`alarm-rule-${value[i].uuid}`}>
              <AlarmItem index={i} value={value[i]} saved={saved?.[i] || null} onChange={onAlarmChange} onDelete={removeAlarm} onRestore={restoreAlarm} disabled={disabled} />
            </Grid>
          ))}
      </Grid>
    ),
    [indexes, value, saved, onAlarmChange, removeAlarm, restoreAlarm, disabled]
  );

  return (
    <Grid container spacing={1} style={{ padding: 8 }}>
      {reports}
      <Grid item xs={12}>
        <Button startIcon={<AddIcon />} onClick={() => addNewAlarm()} disabled={disabled}>
          {formatMessage({ defaultMessage: "New alarm" })}
        </Button>
      </Grid>
    </Grid>
  );
};

export default AlarmList;
